/**
 * Channels Hooks
 * TanStack Query hooks for channel operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as channelsService from '@/services/channels.service';
import type { Channel } from '@/types/api.types';

/**
 * Query hook to list all channels
 */
export const useChannels = () => {
  return useQuery({
    queryKey: ['channels'],
    queryFn: () => channelsService.listChannels(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook to get a single channel
 */
export const useChannel = (channelId: string | null) => {
  return useQuery({
    queryKey: ['channels', channelId],
    queryFn: () => channelsService.getChannel(channelId!),
    enabled: !!channelId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Mutation hook to create a channel
 */
export const useCreateChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      type: 'public' | 'private' | 'announcement';
    }) => channelsService.createChannel(data),
    onSuccess: (newChannel) => {
      // Add new channel to the list
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return [newChannel];
        return [...old, newChannel];
      });

      // Invalidate to refetch with updated data
      queryClient.invalidateQueries({ queryKey: ['channels'] });

      toast.success('Каналът е създаден');
    },
    onError: (error) => {
      console.error('Failed to create channel:', error);
      toast.error('Грешка при създаване на канал');
    },
  });
};

/**
 * Mutation hook to update a channel
 */
export const useUpdateChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      channelId,
      data,
    }: {
      channelId: string;
      data: {
        name?: string;
        description?: string;
        type?: 'public' | 'private' | 'announcement';
        position?: number;
      };
    }) => channelsService.updateChannel(channelId, data),
    onSuccess: (updatedChannel) => {
      // Update channel in list
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return [updatedChannel];
        return old.map((channel) =>
          channel.id === updatedChannel.id ? updatedChannel : channel
        );
      });

      // Update single channel cache
      queryClient.setQueryData(['channels', updatedChannel.id], updatedChannel);

      toast.success('Каналът е актуализиран');
    },
    onError: (error) => {
      console.error('Failed to update channel:', error);
      toast.error('Грешка при актуализиране на канал');
    },
  });
};

/**
 * Mutation hook to delete a channel
 */
export const useDeleteChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => channelsService.deleteChannel(channelId),
    onSuccess: (_, channelId) => {
      // Remove channel from list
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return [];
        return old.filter((channel) => channel.id !== channelId);
      });

      // Remove single channel cache
      queryClient.removeQueries({ queryKey: ['channels', channelId] });

      // Remove messages for this channel
      queryClient.removeQueries({ queryKey: ['messages', channelId] });

      toast.success('Каналът е изтрит');
    },
    onError: (error) => {
      console.error('Failed to delete channel:', error);
      toast.error('Грешка при изтриване на канал');
    },
  });
};

/**
 * Mutation hook to archive a channel
 */
export const useArchiveChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => channelsService.archiveChannel(channelId),
    onSuccess: (archivedChannel) => {
      // Update channel in list
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return [archivedChannel];
        return old.map((channel) =>
          channel.id === archivedChannel.id ? archivedChannel : channel
        );
      });

      // Update single channel cache
      queryClient.setQueryData(['channels', archivedChannel.id], archivedChannel);

      toast.success('Каналът е архивиран');
    },
    onError: (error) => {
      console.error('Failed to archive channel:', error);
      toast.error('Грешка при архивиране на канал');
    },
  });
};

/**
 * Mutation hook to unarchive a channel
 */
export const useUnarchiveChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => channelsService.unarchiveChannel(channelId),
    onSuccess: (unarchivedChannel) => {
      // Update channel in list
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return [unarchivedChannel];
        return old.map((channel) =>
          channel.id === unarchivedChannel.id ? unarchivedChannel : channel
        );
      });

      // Update single channel cache
      queryClient.setQueryData(['channels', unarchivedChannel.id], unarchivedChannel);
    },
  });
};

/**
 * Mutation hook to join a channel
 */
export const useJoinChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => channelsService.joinChannel(channelId),
    onSuccess: (_, channelId) => {
      // Invalidate channels list to refetch with new membership
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['channels', channelId] });
    },
  });
};

/**
 * Mutation hook to leave a channel
 */
export const useLeaveChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => channelsService.leaveChannel(channelId),
    onSuccess: (_, channelId) => {
      // Remove channel from list (user is no longer a member)
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return [];
        return old.filter((channel) => channel.id !== channelId);
      });

      // Remove channel and messages cache
      queryClient.removeQueries({ queryKey: ['channels', channelId] });
      queryClient.removeQueries({ queryKey: ['messages', channelId] });
    },
  });
};

/**
 * Mutation hook to mark a channel as read
 */
export const useMarkChannelAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => channelsService.markChannelAsRead(channelId),
    onSuccess: (_, channelId) => {
      // Optimistically update channel's unread_count to 0
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return [];
        return old.map((channel) =>
          channel.id === channelId
            ? { ...channel, unread_count: 0 }
            : channel
        );
      });
    },
  });
};

/**
 * Query hook to get channel members
 */
export const useChannelMembers = (channelId: string | null) => {
  return useQuery({
    queryKey: ['channel-members', channelId],
    queryFn: () => channelsService.getChannelMembers(channelId!),
    enabled: !!channelId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Mutation hook to add a member to a channel
 */
export const useAddChannelMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      channelId,
      userId,
      role = 'member',
    }: {
      channelId: string;
      userId: string;
      role?: 'member' | 'admin';
    }) => channelsService.addChannelMember(channelId, userId, role),
    onSuccess: (_, { channelId }) => {
      // Invalidate channel members to refetch
      queryClient.invalidateQueries({ queryKey: ['channel-members', channelId] });

      // Update channel's member_count
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return [];
        return old.map((channel) =>
          channel.id === channelId
            ? { ...channel, member_count: (channel.member_count || 0) + 1 }
            : channel
        );
      });

      toast.success('Потребителят е добавен към канала');
    },
    onError: (error) => {
      console.error('Failed to add channel member:', error);
      toast.error('Грешка при добавяне на член');
    },
  });
};

/**
 * Mutation hook to remove a member from a channel
 */
export const useRemoveChannelMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      channelId,
      userId,
    }: {
      channelId: string;
      userId: string;
    }) => channelsService.removeChannelMember(channelId, userId),
    onSuccess: (_, { channelId }) => {
      // Invalidate channel members to refetch
      queryClient.invalidateQueries({ queryKey: ['channel-members', channelId] });

      // Update channel's member_count
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return [];
        return old.map((channel) =>
          channel.id === channelId
            ? { ...channel, member_count: Math.max((channel.member_count || 1) - 1, 0) }
            : channel
        );
      });

      toast.success('Потребителят е премахнат от канала');
    },
    onError: (error) => {
      console.error('Failed to remove channel member:', error);
      toast.error('Грешка при премахване на член');
    },
  });
};

/**
 * Mutation hook to update channel positions
 */
export const useUpdateChannelPositions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (positions: { id: string; position: number }[]) =>
      channelsService.updateChannelPositions(positions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (error) => {
      console.error('Failed to update channel positions:', error);
      toast.error('Грешка при актуализиране на позициите');
    },
  });
};

/**
 * Mutation hook to set default channel
 */
export const useSetDefaultChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => channelsService.setDefaultChannel(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      toast.success('Каналът по подразбиране е зададен');
    },
    onError: (error) => {
      console.error('Failed to set default channel:', error);
      toast.error('Грешка при задаване на канал по подразбиране');
    },
  });
};
